/**
 * useComparisonDiff Hook
 *
 * Calculates field-by-field differences between two records.
 * Supports grouping by form sections with change count tracking.
 */

import { useMemo } from 'react'
import type {
  FieldDifference,
  SectionGroup,
  UseComparisonDiffResult,
  UseComparisonDiffOptions,
} from './types'
import {
  shouldExcludeFromComparison,
  areValuesEqual,
  isNumeric,
  calculateNumericDelta,
} from '@/utils/comparisonUtils'
import { formatSectionTitle } from './utils/formatters'

/**
 * Hook for calculating field-by-field differences between two records.
 *
 * @example
 * ```tsx
 * const {
 *   sections,
 *   changedCount,
 *   hasDifferences,
 *   keyFieldChanges
 * } = useComparisonDiff({
 *   leftRecord,
 *   rightRecord,
 *   fields: metadata.fields,
 *   formSections: metadata.formSections,
 *   comparison: { keyFields: ['price', 'cost'] },
 * })
 * ```
 */
export function useComparisonDiff({
  leftRecord,
  rightRecord,
  fields,
  formSections = [],
  comparison,
  showDifferencesOnly = false,
}: UseComparisonDiffOptions): UseComparisonDiffResult {
  return useMemo(() => {
    // Need at least one record to display anything
    // The "primary" record is whichever one exists (left takes precedence for display)
    const primaryRecord = leftRecord ?? rightRecord
    const comparisonRecord = leftRecord ? rightRecord : null

    if (!primaryRecord) {
      return {
        differences: [],
        changedFields: [],
        unchangedFields: [],
        changedCount: 0,
        unchangedCount: 0,
        hasDifferences: false,
        keyFieldChanges: [],
        sections: [],
      }
    }

    // Build explicit exclude set from comparison config
    const configExcludeFields = new Set(comparison?.excludeFields ?? [])

    // Key fields set for identifying business-critical changes
    const keyFields = new Set(comparison?.keyFields ?? [])

    // Get all unique field keys from records and metadata
    const allFieldKeys = new Set<string>([
      ...Object.keys(primaryRecord),
      ...(comparisonRecord ? Object.keys(comparisonRecord) : []),
      ...Object.keys(fields),
    ])

    // Calculate differences for each field
    const allDifferences: FieldDifference[] = []

    for (const fieldKey of allFieldKeys) {
      // Skip fields excluded by comparison config
      if (configExcludeFields.has(fieldKey)) {
        continue
      }

      // Skip fields not in metadata (internal fields)
      const fieldMeta = fields[fieldKey]
      if (!fieldMeta) {
        continue
      }

      // Skip fields that should be excluded based on category (system, hidden, etc.)
      if (shouldExcludeFromComparison(fieldKey, fieldMeta)) {
        continue
      }

      // Left column shows primary record, right column shows comparison record
      const leftValue = primaryRecord[fieldKey]
      const rightValue = comparisonRecord ? comparisonRecord[fieldKey] : undefined

      // When no comparison record, treat all fields as "unchanged" (no highlighting)
      const isDifferent = comparisonRecord !== null
        ? !areValuesEqual(leftValue, rightValue)
        : false

      // Calculate numeric deltas for numeric fields
      const isNumericField =
        fieldMeta.py_type === 'numeric' ||
        fieldMeta.py_type === 'integer' ||
        fieldMeta.py_type === 'float' ||
        (comparisonRecord && isNumeric(leftValue) && isNumeric(rightValue))

      const { numericDelta, percentDelta } = comparisonRecord && isNumericField
        ? calculateNumericDelta(rightValue, leftValue)
        : {}

      allDifferences.push({
        fieldKey,
        leftValue,
        rightValue,
        isDifferent,
        numericDelta,
        percentDelta,
      })
    }

    // Sort differences: key fields first, then by field name
    allDifferences.sort((a, b) => {
      const aIsKey = keyFields.has(a.fieldKey)
      const bIsKey = keyFields.has(b.fieldKey)
      if (aIsKey && !bIsKey) return -1
      if (!aIsKey && bIsKey) return 1
      return a.fieldKey.localeCompare(b.fieldKey)
    })

    // Split into changed and unchanged
    const changedFields = allDifferences.filter(d => d.isDifferent)
    const unchangedFields = allDifferences.filter(d => !d.isDifferent)

    // Find key field changes
    const keyFieldChanges = changedFields.filter(d => keyFields.has(d.fieldKey))

    // Group differences by form section
    const sectionMap = new Map<string, FieldDifference[]>()

    // Initialize sections from metadata
    for (const section of formSections) {
      sectionMap.set(section.name, [])
    }

    // Always have an "other" section for uncategorized fields
    sectionMap.set('other', [])

    // Apply showDifferencesOnly filter
    // Only filter when there's actually a comparison - if no comparison record,
    // always show all fields (there are no "differences" without comparison)
    const differencesToGroup = (showDifferencesOnly && comparisonRecord)
      ? changedFields
      : allDifferences

    // Group by section
    for (const diff of differencesToGroup) {
      const fieldMeta = fields[diff.fieldKey]
      const sectionName = fieldMeta?.form_section || 'other'

      if (!sectionMap.has(sectionName)) {
        sectionMap.set(sectionName, [])
      }
      sectionMap.get(sectionName)!.push(diff)
    }

    // Build section groups with ordering
    const sectionOrder = new Map(formSections.map((s, i) => [s.name, s.order ?? i]))
    const sections: SectionGroup[] = []

    for (const [name, sectionFields] of sectionMap) {
      // Skip empty sections
      if (sectionFields.length === 0) continue

      const formSection = formSections.find(s => s.name === name)
      const changedInSection = sectionFields.filter(f => f.isDifferent).length

      sections.push({
        key: name,
        title: formSection?.description || formatSectionTitle(name),
        description: formSection?.description ?? undefined,
        order: sectionOrder.get(name) ?? 999,
        fields: sectionFields,
        changedCount: changedInSection,
      })
    }

    // Sort sections by order
    sections.sort((a, b) => a.order - b.order)

    return {
      differences: (showDifferencesOnly && comparisonRecord) ? changedFields : allDifferences,
      changedFields,
      unchangedFields,
      changedCount: changedFields.length,
      unchangedCount: unchangedFields.length,
      hasDifferences: changedFields.length > 0,
      keyFieldChanges,
      sections,
    }
  }, [leftRecord, rightRecord, fields, formSections, comparison, showDifferencesOnly])
}

export default useComparisonDiff
